import { createContext, ReactNode, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './Modal';

/**
 * App-wide imperative dialogs that REPLACE the native browser
 * `window.confirm` / `window.alert` / `window.prompt` (which can't be styled,
 * localized, or themed). Mounted once at the root; call via `useDialogs()`:
 *
 *   const { confirm, alert } = useDialogs();
 *   if (await confirm({ title: 'Delete?', destructive: true })) doDelete();
 *   await alert({ title: 'Done.' });
 *
 * `requireText` reproduces a typed-confirmation prompt (e.g. type "PURGE").
 */
export interface ConfirmOptions {
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Danger styling on the confirm button. Defaults to true. */
  destructive?: boolean;
  /** When set, the confirm button stays disabled until the user types this exact string. */
  requireText?: string;
}

export interface AlertOptions {
  title: ReactNode;
  description?: ReactNode;
  okLabel?: string;
}

interface DialogApi {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  alert: (opts: AlertOptions) => Promise<void>;
}

type Pending =
  | { kind: 'confirm'; opts: ConfirmOptions; resolve: (v: boolean) => void }
  | { kind: 'alert'; opts: AlertOptions; resolve: () => void };

const DialogContext = createContext<DialogApi | null>(null);

export function useDialogs(): DialogApi {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useDialogs must be used within <DialogProvider>');
  return ctx;
}

export function DialogProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<Pending | null>(null);
  const [typed, setTyped] = useState('');

  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setTyped('');
        setPending({ kind: 'confirm', opts, resolve });
      }),
    [],
  );

  const alert = useCallback(
    (opts: AlertOptions) => new Promise<void>((resolve) => setPending({ kind: 'alert', opts, resolve })),
    [],
  );

  const settle = (result: boolean) => {
    setPending((p) => {
      if (p) {
        if (p.kind === 'confirm') p.resolve(result);
        else p.resolve();
      }
      return null;
    });
  };

  const isConfirm = pending?.kind === 'confirm';
  const confirmOpts = isConfirm ? (pending.opts as ConfirmOptions) : undefined;
  const requireText = confirmOpts?.requireText;
  const confirmDisabled = !!requireText && typed !== requireText;

  return (
    <DialogContext.Provider value={{ confirm, alert }}>
      {children}
      <Modal
        open={!!pending}
        onOpenChange={(next) => {
          if (!next) settle(false);
        }}
        title={pending?.opts.title}
        description={pending?.opts.description}
        size="sm"
      >
        {requireText && (
          <input
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={requireText}
            className="mb-4 w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          />
        )}
        <div className="flex justify-end gap-2">
          {isConfirm && (
            <button type="button" onClick={() => settle(false)} className="btn-ghost px-3.5 py-2 text-sm">
              {confirmOpts?.cancelLabel ?? (t('common.cancel', { defaultValue: 'Cancel' }) as string)}
            </button>
          )}
          <button
            type="button"
            onClick={() => settle(true)}
            disabled={confirmDisabled}
            className={
              isConfirm && confirmOpts?.destructive !== false
                ? 'inline-flex items-center justify-center px-3.5 py-2 rounded-lg text-sm font-semibold transition bg-warn-500 text-white hover:bg-warn-600 shadow-press disabled:opacity-50'
                : 'btn-press px-3.5 py-2 text-sm disabled:opacity-50'
            }
          >
            {isConfirm
              ? confirmOpts?.confirmLabel ?? (t('common.confirm', { defaultValue: 'Confirm' }) as string)
              : (pending?.kind === 'alert' && pending.opts.okLabel) || (t('common.ok', { defaultValue: 'OK' }) as string)}
          </button>
        </div>
      </Modal>
    </DialogContext.Provider>
  );
}
